import React, {useMemo, useState} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';

import {useAlert} from 'src/shared/hooks/useAlert';

import {useDisclosure} from '@mantine/hooks';
import type {ItemDefinitionRequest} from 'src/api/ProactApiClient';
import {
  AttachmentModuleEnum,
  EntityIdentifierEnum,
  ModuleEnum,
} from 'src/api/ProactApiClient';
import {
  useCreateItemDefinitionMutation,
  useDeleteItemDefinitionMutation,
  useGetItemDefinitionQuery,
  useUpdateItemDefinitionMutation,
} from 'src/redux/queries/itemDefinition';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkItemTracker} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {useExtensionTabs} from 'src/shared/hooks/useExtensionTabs';
import {useItemTrackerBreadcrumbs} from 'src/shared/hooks/useItemTrackerBreadcrumbs';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import type {IRouteService} from 'src/shared/services/RouterService/IRouteService';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {AttachmentsModule} from 'src/app/attachments/AttachmentsModule';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {ItemAssignmentsTable} from './ItemAssignmentsTable';
import {
  CreateDefaultItemDefinition,
  ItemDefinitionMeta,
} from './itemDefinitionHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export type ItemDefinitionViewType =
  | 'content'
  | 'attachments'
  | 'audit'
  | ''
  | ':view';

export interface ItemDefinitionFormProps {
  id?: number;
  mode: 'create' | 'view' | 'edit';
  view?: ItemDefinitionViewType;
  router: IRouteService;
}

export const ItemDefinitionForm: React.FC<ItemDefinitionFormProps> = ({
  id,
  mode,
  view = 'content',
  router,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const itemDefPerms = useModulePermissions(ModuleEnum.ItemTrackerDefinitions);
  const auditPerms = useAuditLogPermissions();

  const formMeta = useMemo(() => ItemDefinitionMeta(), []);

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);

  const {data: item, isLoading} = useGetItemDefinitionQuery(id ?? 0, {
    skip: id === undefined || mode === 'create',
  });

  const form = useEntitySyncedForm({
    formMeta,
    initialData: CreateDefaultItemDefinition(),
    entity: item,
    toFormData: entity =>
      toFormDataV2({...CreateDefaultItemDefinition(), ...entity}),
  });

  const [isSavePending, setIsSavePending] = useState(false);

  const [triggerCreate] = useCreateItemDefinitionMutation();
  const [triggerUpdate] = useUpdateItemDefinitionMutation();
  const [triggerDelete] = useDeleteItemDefinitionMutation();

  const handleSave = async () => {
    try {
      setIsSavePending(true);
      const body = {
        ...item,
        ...form.getFieldValues(),
      } as ItemDefinitionRequest;

      const res =
        id === undefined
          ? await triggerCreate(body).unwrap()
          : await triggerUpdate(body).unwrap();

      setAlert('Item was successfully updated', 'success');

      navigate(router.getViewRoute(String(res.id)), {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const handleEdit = () => navigate(router.getEditRoute(String(id)));

  const handleDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      await triggerDelete(+id!).unwrap();

      setAlert('Item definition was successfully deleted.', 'success');

      navigate(PathUtility.ItemDefinitions());
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const canSave =
    (mode === 'create' && itemDefPerms.create) ||
    (mode === 'edit' && itemDefPerms.update);
  const canEdit = mode === 'view' && itemDefPerms.update;
  const canDelete = mode === 'view' && itemDefPerms.delete;

  const extensionTabs = useExtensionTabs(
    ModuleEnum.ItemTrackerDefinitions,
    id ?? 0
  );

  const {RenderedTabsComponent} = useUrlQueryTabV2<ItemDefinitionViewType>({
    view: (view ?? '') as ItemDefinitionViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: (
          <>
            {mode !== 'view' ? (
              <>
                <ProactForm form={form}>
                  <ProactFormGrid />
                </ProactForm>
                <SaveButton
                  loading={isSavePending}
                  onClick={handleValidateAndSave}
                />
              </>
            ) : (
              <>
                <FormViewComponentV2
                  formData={toFormDataV2(item)}
                  formMeta={formMeta}
                />
                <ItemAssignmentsTable
                  itemDefinitionId={String(id)}
                  itemDefinitionLabel={item?.name ?? 'item'}
                />
              </>
            )}
          </>
        ),
      },
      {
        view: 'attachments',
        label: 'Attachments',
        disabled: mode !== 'view' || !itemDefPerms.attachments,
        component: (
          <>
            {item?.id && (
              <AttachmentsModule
                attachmentType={AttachmentModuleEnum.ItemDefinition}
                entityId={item.id}
              />
            )}
          </>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: mode !== 'view' || !auditPerms.view,
        component: item?.id && (
          <AuditModule
            entityId={String(item.id)}
            entityType={EntityIdentifierEnum.TaskDefinition}
          />
        ),
      },
      ...extensionTabs,
    ],
    onSelectView: view => {
      router.setParameter('view', view);
      navigate(router.getViewRoute(String(id)), {replace: true});
    },
  });

  const getBreadcrumbMode = () => {
    switch (mode) {
      case 'create':
        return 'definition-create';
      case 'edit':
        return 'definition-edit';
      default:
        return 'definition-view';
    }
  };

  const {breadcrumbs, fallbackTo} = useItemTrackerBreadcrumbs({
    mode: getBreadcrumbMode(),
    definitionId: id,
    itemDefinitionName: item?.name,
  });

  return isLoading === false ? (
    <>
      <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkItemTracker.icon}>
          {item ? `${item?.name}` : 'New Item'}
        </PageHeader.Title>
        {item?.internalIdentifier && (
          <PageHeader.Meta>{item.internalIdentifier}</PageHeader.Meta>
        )}
        {mode === 'edit' && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}
        <PageHeader.Actions>
          {canEdit && (
            <PageHeader.Action variant="edit" onClick={handleEdit}>
              Edit
            </PageHeader.Action>
          )}
          {canDelete && (
            <PageHeader.Action
              variant="delete"
              onClick={() => confirmDeleteHandlers.open()}>
              Delete
            </PageHeader.Action>
          )}
          {canSave && (
            <PageHeader.Action
              variant="save"
              onClick={handleValidateAndSave}
              loading={isSavePending}>
              Save
            </PageHeader.Action>
          )}
        </PageHeader.Actions>
      </PageHeader>

      {RenderedTabsComponent}

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        title="Are you sure you want to delete this item definition?"
        body="This action cannot be undone. All data relating to this record will also be deleted, including all assignments, notes, and notification history."
      />
    </>
  ) : (
    <TableBodyRender isLoading={isLoading} />
  );
};
